import React, { useMemo, useCallback } from 'react';
import { always, equals, identity, ifElse, sum } from 'ramda';
import { AxisLeft, AxisRight, AxisBottom } from '@vx/axis';
import { useIntl } from 'gatsby-plugin-intl';
import { renderWhenTrue } from '../../../utils/rendering';
import { CHART_AXIS_LABEL_COLOR, CHART_AXIS_LIGHT_LABEL_COLOR } from '../../../utils/colors';
import { numTicksForHeight } from '../charts.helper';
import { CHART_TYPES } from '../charts.constants';
import { AxesWrapper, valueAxisStyle, groupAxisStyle, tickStyle, leftTickStyle, rightTickStyle } from './axes.styles';
import { X_BOTTOM_OFFSET, TICK_ROTATE_DEG, TICK_LABEL_FONT_SIZE } from './axes.constants';
import { Group } from '@vx/group';
import { Line } from '@vx/shape';
import { measureNoWrappedText } from '../../../utils/measureText';

const CustomBottomAxis = ({ chartWidth, props }) => {
  const axisCenter = (props.axisToPoint.x - props.axisFromPoint.x) / 2;
  const labelWidths = useMemo(
    () =>
      props.ticks.map((tick) => {
        const { width } = measureNoWrappedText(tick.value);
        return width;
      }),
    [props.ticks]
  );

  const shouldItBeRotated = useCallback(() => {
    return sum(labelWidths) > chartWidth;
  }, [chartWidth, labelWidths]);
  const { formatMessage } = useIntl();
  return (
    <g className="my-custom-bottom-axis">
      {props.ticks.map((tick, i) => {
        const tickX = tick.to.x;
        const tickY = tick.to.y + TICK_LABEL_FONT_SIZE + props.tickLength;

        return (
          <Group key={`vx-tick-${tick.value}-${i}`} className={'vx-axis-tick'}>
            <Line from={tick.from} to={tick.to} stroke={CHART_AXIS_LABEL_COLOR} />
            <text
              transform={`translate(${tickX}, ${tickY}) rotate(${shouldItBeRotated(labelWidths) ? TICK_ROTATE_DEG : 0
                })`}
              fontSize={TICK_LABEL_FONT_SIZE}
              textAnchor="middle"
              fill={CHART_AXIS_LABEL_COLOR}
            >
              {tick.formattedValue==='' ? tick.formattedValue : formatMessage({id: tick.formattedValue})}
            </text>
          </Group>
        );
      })}
      <text textAnchor="middle" transform={`translate(${axisCenter}, 50)`} fontSize="8">
        {props.label}
      </text>
    </g>
  );
};

const getAxesProps = ({ type, xAxisVisible, yAxisVisible, yAxisRightSide, groupScale, valueScale }) => {
  const axesProps = {};
  const sideTickStyle = ifElse(identity, always(rightTickStyle), always(leftTickStyle))(yAxisRightSide);

  ifElse(
    equals(CHART_TYPES.HORIZONTAL),
    () => {
      if (yAxisVisible) {
        axesProps.leftAxisProps = {
          scale: groupScale,
          tickLabelProps: () => ({
            fill: CHART_AXIS_LABEL_COLOR,
            ...tickStyle,
            ...sideTickStyle,
          }),
          ...groupAxisStyle,
        };
      }
      if (xAxisVisible) {
        axesProps.bottomAxisProps = {
          scale: valueScale,
          tickLabelProps: () => ({
            fill: CHART_AXIS_LIGHT_LABEL_COLOR,

            ...tickStyle,
          }),
          ...valueAxisStyle,
        };
      }
    },
    () => {
      if (yAxisVisible) {
        axesProps.leftAxisProps = {
          scale: valueScale,
          tickLabelProps: () => ({
            fill: CHART_AXIS_LIGHT_LABEL_COLOR,
            ...tickStyle,
            ...sideTickStyle,
          }),
          ...valueAxisStyle,
        };
      }
      if (xAxisVisible) {
        axesProps.bottomAxisProps = {
          scale: groupScale,
          tickLabelProps: () => ({
            fill: CHART_AXIS_LABEL_COLOR,
            ...tickStyle,
          }),
          ...groupAxisStyle,
        };
      }
    }
  )(type);

  return axesProps;
};

export const Axes = ({ groupScale, valueScale, type, chartHeight, chartWidth, config, className, ...props }) => {
  const { xAxisVisible, yAxisVisible, yAxisRightSide } = config;
  const axesProps = getAxesProps({ type, xAxisVisible, yAxisVisible, yAxisRightSide, groupScale, valueScale });

  const renderAxisLeft = renderWhenTrue(() => (
    <AxisLeft left={config.xAxisSpace} numTicks={numTicksForHeight(chartHeight)} {...axesProps.leftAxisProps} />
  ));

  const renderAxisRight = renderWhenTrue(() => (
    <AxisRight left={chartWidth} numTicks={numTicksForHeight(chartHeight)} {...axesProps.leftAxisProps} />
  ));

  const renderAxisBottom = renderWhenTrue(() => (
    <AxisBottom id="bottom" top={chartHeight} left={config.xAxisSpace - X_BOTTOM_OFFSET} {...axesProps.bottomAxisProps}>
      {(props) => {
        return <CustomBottomAxis props={props} chartWidth={chartWidth} />;
      }}
    </AxisBottom>
  ));

  return (
    <AxesWrapper config={config} className={className} {...props}>
      {renderAxisLeft(yAxisVisible && !yAxisRightSide)}
      {renderAxisRight(yAxisVisible && yAxisRightSide)}
      {renderAxisBottom(xAxisVisible)}
    </AxesWrapper>
  );
};
